import React from "react";
import EnumSelectorInput from "../../common/inputs/EnumSelectorInput";
import ValueReset from "../../common/inputs/ValueReset";
import { DatabaseContext } from "../../database/DatabaseHandler";
import IHardware from "../../database/interfaces/IHardware";
import { IHardwareParamTypeDescriptor } from "../../database/interfaces/paramScript/IParamTypeDescriptor";
import { fieldAccess } from "../../parametersProcessor/paramAccess";
import { fieldDefault } from "../../parametersProcessor/paramDefaults";

interface IHardwareCardContentProps {
    typeDescriptor: IHardwareParamTypeDescriptor;
    paramValue: any;
    storedDefault?: any;
    onUpdateParamValue: (value: any) => void;
}

interface IHardwareGroup {
    key: string;
    label: string;
    members: IHardware[];
}

const NONE = "None";

const groupByFamily = (hardware: IHardware[], kind: string): IHardwareGroup[] => {
    const groups = new Map<string, IHardwareGroup>();
    for (const hw of hardware) {
        // Csak családdal bíró vasalat választható (a család nélküli sor fejlesztői/kísérő elem).
        if (hw.Kind !== kind || !hw.Family) continue;
        const key = hw.Family;
        const group = groups.get(key)
            ?? { key, label: hw.Family.trim(), members: [] };
        group.members.push(hw);
        groups.set(key, group);
    }
    return [...groups.values()];
};

const HardwareCardContent: React.FC<IHardwareCardContentProps> = ({
    typeDescriptor, paramValue, storedDefault, onUpdateParamValue,
}) => {
    const d = typeDescriptor;
    const { hardwareData } = React.useContext(DatabaseContext);
    const cur = (paramValue && typeof paramValue === "object") ? paramValue : {};
    const access = fieldAccess(d);

    const groups = React.useMemo(
        () => groupByFamily(hardwareData ?? [], d?.kind ?? ""),
        [hardwareData, d?.kind]
    );

    // A „nincs vasalat" az ELSŐ opció, ha a descriptor adott neki címkét — így a be/ki kapcsoló és a
    // típusválasztó egyetlen mező (a felhasználónak a „függesztő nélkül" is csak egy választás).
    const options = React.useMemo(
        () => Object.fromEntries([
            ...(d?.none ? [[NONE, d.none]] : []),
            ...groups.map((g) => [g.key, g.label]),
        ]),
        [groups, d?.none]
    );


    const selected = React.useMemo(() => {
        const ids = Object.keys(d?.sides ?? {}).map((key) => cur[key]).filter(Boolean);
        return groups.find((g) => g.members.some((hw) => ids.includes(hw.ID)))?.key
            ?? (d?.none ? NONE : "");
    }, [groups, cur, d?.sides, d?.none]);


    const emit = (groupKey: string) => {
        // Üres objektum = „nincs vasalat": a generateStyleScript az ilyen paramétert ki sem küldi,
        // tehát a konstansok (pl. BackMargin) sem mehetnek bele.
        if (groupKey === NONE) return onUpdateParamValue({});

        const group = groups.find((g) => g.key === groupKey);
        const next: Record<string, any> = { ...(d?.constants ?? {}) };
        for (const [key, side] of Object.entries(d?.sides ?? {})) {
            const member = side
                ? group?.members.find((hw) => hw.Side === side)
                : group?.members[0];
            next[key] = member?.ID ?? "";
        }
        onUpdateParamValue(next);
    };

    const defaultKey = React.useMemo(() => {
        const ids = Object.keys(d?.sides ?? {}).map((key) => storedDefault?.[key]).filter(Boolean);
        return groups.find((g) => g.members.some((hw) => ids.includes(hw.ID)))?.key
            ?? (d?.none ? NONE : undefined);
    }, [groups, storedDefault, d?.sides, d?.none]);

    return d ? (
        <ValueReset
            paramDefault={access.resettable ? fieldDefault({ options, default: defaultKey }, undefined) : null}
            currentValue={selected}
            onReset={emit}
        >
            <EnumSelectorInput
                label={d.label}
                value={selected}
                options={options}
                tooltip={d.tooltip}
                editable={access.editable}
                readOnly={access.readOnly}
                onValueChange={emit}
            />
        </ValueReset>
    ) : null;
};

export default HardwareCardContent;
